import { useState, useEffect, useRef } from "react";
import { Outlet, NavLink, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Briefcase,
  HandCoins,
  CalendarDays,
  Share2,
  BarChart3,
  Settings as SettingsIcon,
  Inbox,
  LogOut,
  Bell,
  Menu,
  X,
  ChevronRight,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import Avatar from "./Avatar";
import api from "../lib/api";
import { relativeTime } from "../lib/format";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/members", label: "Members", icon: Users },
  { to: "/opportunities", label: "Opportunities", icon: Briefcase },
  { to: "/deals", label: "Deals", icon: HandCoins },
  { to: "/meetings", label: "Meetings", icon: CalendarDays },
  { to: "/synergy-map", label: "Synergy Map", icon: Share2 },
  { to: "/quarterly-review", label: "Quarterly Review", icon: BarChart3 },
];

function SidebarLink({ to, label, icon: Icon, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      data-testid={`nav-${to.replace("/", "")}`}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-4 py-2.5 rounded-md text-sm transition-all duration-200 ${
          isActive
            ? "bg-[rgba(201,168,76,0.08)] text-[#C9A84C] border-l-2 border-[#C9A84C] pl-[14px]"
            : "text-[#9A9590] hover:text-[#F0EDE8] hover:bg-[rgba(201,168,76,0.04)]"
        }`
      }
    >
      <Icon size={18} strokeWidth={1.5} />
      <span className="tracking-wide">{label}</span>
    </NavLink>
  );
}

function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const ref = useRef(null);
  const unread = notifs.filter((n) => !n.read).length;

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data);
    } catch (e) {
      // Polling failure is benign — next tick retries.
      console.warn("Notifications poll failed", e);
    }
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notifications-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-full text-[#9A9590] hover:text-[#C9A84C] transition-colors"
        aria-label="Notifications"
      >
        <Bell size={20} strokeWidth={1.5} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#C9A84C] shadow-[0_0_8px_#C9A84C]" />
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-[480px] overflow-y-auto ts-card shadow-xl z-50" data-testid="notifications-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#C9A84C]/15">
            <div className="overline">Notifications</div>
            <button
              onClick={markAll}
              className="text-xs text-[#9A9590] hover:text-[#C9A84C] transition-colors"
              data-testid="mark-all-read"
            >
              Mark all read
            </button>
          </div>
          {notifs.length === 0 ? (
            <div className="px-4 py-8 text-sm text-[#9A9590] text-center">No notifications yet.</div>
          ) : (
            <ul>
              {notifs.map((n) => (
                <li
                  key={n.id}
                  className={`px-4 py-3 border-b border-[#C9A84C]/10 hover:bg-[rgba(201,168,76,0.04)] transition ${
                    !n.read ? "bg-[rgba(201,168,76,0.03)]" : ""
                  }`}
                >
                  <Link to={n.link || "#"} onClick={() => setOpen(false)} className="block">
                    <div className="text-sm text-[#F0EDE8]">{n.title}</div>
                    <div className="text-xs text-[#9A9590] mt-0.5">{n.body}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#9A9590]/70 mt-1">
                      {relativeTime(n.created_at)}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-[#F0EDE8] flex">
      {/* Sidebar — desktop */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-[#C9A84C]/12 bg-[#0D0D0D] flex-col">
        <div className="px-6 pt-8 pb-6 border-b border-[#C9A84C]/12">
          <Link to="/dashboard" className="block">
            <div className="font-serif text-[#C9A84C] uppercase tracking-[0.32em] text-sm leading-tight">
              The Synergy
            </div>
            <div className="font-serif text-[#C9A84C] uppercase tracking-[0.32em] text-sm leading-tight">
              Table
            </div>
          </Link>
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1">
          {NAV.map((n) => <SidebarLink key={n.to} {...n} />)}
          {user?.role === "admin" && (
            <>
              <SidebarLink to="/applications" label="Applications" icon={Inbox} />
              <SidebarLink to="/settings" label="Settings" icon={SettingsIcon} />
            </>
          )}
        </nav>
        <div className="p-3 border-t border-[#C9A84C]/12">
          <Link to="/profile" className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-[rgba(201,168,76,0.04)] transition" data-testid="profile-link">
            <Avatar name={user?.name} photoUrl={user?.photo_url} color={user?.avatar_color || "#C9A84C"} size={36} />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-[#F0EDE8] truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-[#C9A84C]">{user?.group_role || user?.role}</div>
            </div>
            <ChevronRight size={14} className="text-[#9A9590]" />
          </Link>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="mt-2 w-full flex items-center gap-3 px-3 py-2 text-sm text-[#9A9590] hover:text-[#C9A84C] transition rounded-md"
          >
            <LogOut size={16} strokeWidth={1.5} />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Mobile menu drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-[#0D0D0D] border-r border-[#C9A84C]/15 flex flex-col">
            <div className="flex items-center justify-between px-5 py-5 border-b border-[#C9A84C]/15">
              <div className="font-serif text-[#C9A84C] uppercase tracking-[0.3em] text-sm">Menu</div>
              <button onClick={() => setMobileOpen(false)} className="text-[#9A9590]" data-testid="mobile-close">
                <X size={20} />
              </button>
            </div>
            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
              {NAV.map((n) => <SidebarLink key={n.to} {...n} onClick={() => setMobileOpen(false)} />)}
              {user?.role === "admin" && (
                <>
                  <SidebarLink to="/applications" label="Applications" icon={Inbox} onClick={() => setMobileOpen(false)} />
                  <SidebarLink to="/settings" label="Settings" icon={SettingsIcon} onClick={() => setMobileOpen(false)} />
                </>
              )}
              <SidebarLink to="/profile" label="My Profile" icon={Users} onClick={() => setMobileOpen(false)} />
            </nav>
            <div className="p-3 border-t border-[#C9A84C]/15">
              <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-[#9A9590]">
                <LogOut size={16} /> Sign out
              </button>
            </div>
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <header
          className="flex items-center justify-between border-b border-[#C9A84C]/12 px-5 lg:px-12 py-4 sticky top-0 z-30 bg-[#0D0D0D]/85 backdrop-blur-md"
          style={{ paddingTop: "max(env(safe-area-inset-top), 16px)" }}
        >
          <div className="flex items-center gap-3 lg:hidden">
            <button onClick={() => setMobileOpen(true)} data-testid="mobile-open" className="text-[#F0EDE8]">
              <Menu size={22} strokeWidth={1.5} />
            </button>
            <Link to="/dashboard" className="font-serif text-[#C9A84C] uppercase tracking-[0.3em] text-xs">
              The Synergy Table
            </Link>
          </div>
          <div className="hidden lg:flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-[#9A9590]">
            <span>Members Only</span>
            <span className="w-1 h-1 rounded-full bg-[#C9A84C]" />
            <span>Dubai</span>
          </div>
          <div className="flex items-center gap-2">
            <NotificationsBell />
            <Link to="/profile" className="lg:hidden">
              <Avatar name={user?.name} photoUrl={user?.photo_url} color={user?.avatar_color || "#C9A84C"} size={32} />
            </Link>
          </div>
        </header>

        <main className="flex-1 px-5 sm:px-8 lg:px-12 py-8 lg:py-12 max-w-7xl w-full mx-auto">
          <Outlet />
        </main>

        <footer
          className="px-5 lg:px-12 py-6 border-t border-[#C9A84C]/10 text-[10px] uppercase tracking-[0.3em] text-[#9A9590]/70 flex flex-col sm:flex-row items-center justify-between gap-2"
          style={{ paddingBottom: "max(env(safe-area-inset-bottom), 24px)" }}
        >
          <span>The Synergy Table · Dubai</span>
          <span>Where the right people close the right deals.</span>
        </footer>
      </div>
    </div>
  );
}
